import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

export type KpiOption = { readAs: string };

/**
 * Prototype-style KPI tile: bold 14px title, light-weight 44px blue figure, muted 15px caption.
 * Optional `options` render the ⋮ menu (e.g. Total / 30 / 60 / 90 days).
 */
@Component({
    selector: 'r1-kpi-card',
    template: `
        <div class="kpi">
            <div class="kpi-head">
                <span class="kpi-title">{{ title() }}</span>
                @if (options().length) {
                    <button
                        mat-icon-button
                        type="button"
                        class="kpi-menu"
                        [matMenuTriggerFor]="menu"
                        aria-label="Options">
                        <mat-icon>more_vert</mat-icon>
                    </button>
                    <mat-menu #menu="matMenu" xPosition="before">
                        @for (option of options(); track option.readAs) {
                            <button mat-menu-item type="button" (click)="optionChange.emit(option)">
                                {{ option.readAs }}
                            </button>
                        }
                    </mat-menu>
                }
            </div>
            <div class="kpi-body">
                <div class="kpi-value" [class.kpi-value--text]="text() !== null">
                    @if (text() !== null) {
                        {{ text() }}
                    } @else if (value() === null || value() === undefined) {
                        —
                    } @else {
                        {{ prefix() }}{{ value() | number: format() }}{{ suffix() }}
                    }
                </div>
                <div class="kpi-label">{{ label() }}</div>
            </div>
        </div>
    `,
    styles: `
        :host {
            display: block;
            height: 100%;
        }
        .kpi {
            height: 100%;
            box-sizing: border-box;
            background: var(--r1-white);
            border-radius: 4px;
            box-shadow:
                0 1px 3px rgba(16, 24, 40, 0.1),
                0 1px 2px rgba(16, 24, 40, 0.06);
            padding: 14px 16px 26px;
        }
        .kpi-head {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            min-height: 24px;
        }
        .kpi-title {
            font-size: 14px;
            font-weight: 700;
            color: #1c1f23;
            line-height: 24px;
        }
        .kpi-menu {
            width: 24px;
            height: 24px;
            padding: 0;
            margin: 0 -6px 0 0;
            color: #9aa3ae;
            mat-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
                line-height: 18px;
            }
        }
        .kpi-body {
            text-align: center;
            margin-top: 22px;
        }
        .kpi-value {
            font-size: 44px;
            font-weight: 300;
            color: var(--r1-brilliant-blue);
            line-height: 1;
            letter-spacing: -0.01em;
        }
        .kpi-value--text {
            font-size: 36px;
        }
        .kpi-label {
            font-size: 15px;
            color: #5c6570;
            margin-top: 10px;
        }
    `,
    imports: [DecimalPipe, MatIconButton, MatIcon, MatMenu, MatMenuItem, MatMenuTrigger],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiCardComponent {
    title = input.required<string>();
    /** Numeric figure; `null` renders an em dash while loading / unavailable. */
    value = input<number | null | undefined>(null);
    /** Pre-formatted figure (e.g. "99.2%") that bypasses the number pipe. */
    text = input<string | null>(null);
    label = input<string>('');
    prefix = input<string>('');
    suffix = input<string>('');
    format = input<string>('1.0-0');
    options = input<KpiOption[]>([]);
    optionChange = output<KpiOption>();
}
